import React from 'react';
import { Button } from '@design-system/Button';

interface FoodInfoCardProps {
  description: string;
  composition: string[];
  macronutrients: { title: string; value: string }[];
  tips: string[];
  'data-testid'?: string;
}

const FoodInfoCard: React.FC<FoodInfoCardProps> = ({
  description,
  composition,
  macronutrients,
  tips,
  'data-testid': testId,
}) => {
  return (
    <section 
      data-testid={testId}
      className="bg-transparent text-gray-900 p-8 rounded-lg shadow-none !pt-20"
    >
      {/* Composition and Macronutrients */}
      <div 
        data-testid={`${testId}-mainContent`}
        className="grid grid-cols-1 md:grid-cols-2 gap-8"
      >
        {/* Composition */}
        <div data-testid={`${testId}-leftColumn`}>
          {/* Header */}
          <h1 
            data-testid={`${testId}-aboutTitle`}
            className="typography-h1 text-[#F4F4F5] mb-6"
          >
            About this Food
          </h1>

          {/* Description */}
          {description && (
            <p 
              data-testid={`${testId}-description`}
              className="typography-h3 text-[#F4F4F5] mb-8"
            >
              {description}
            </p>
          )}
          {(composition?.length > 0) && (
            <>
              <h2 
                data-testid={`${testId}-compositionTitle`}
                className="typography-h3 text-[#F4F4F5] mb-4"
              >
                Composition
              </h2>
              <div 
                data-testid={`${testId}-compositionList`}
                className="flex flex-wrap gap-2 pb-24"
              >
                {composition?.map((item, index) => (
                  <Button
                    key={item}
                    data-testid={`${testId}-compositionItem-${index}`}
                    variant="ghost"
                    size="sm"
                    className="cursor-default text-white border border-stroke-static px-3.5 py-0 h-8 rounded-[0.5rem]"
                  >
                    {item}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Macronutrients */}
        {macronutrients?.length > 0 && (
          <div data-testid={`${testId}-rightColumn`}>
            <h2 
              data-testid={`${testId}-macronutrientsTitle`}
              className="typography-h3 text-[#F4F4F5] mb-4"
            >
              Macronutrients per serving
            </h2>
            <table 
              data-testid={`${testId}-macronutrientsTable`}
              className="w-full border-collapse"
            >
              <tbody>
                {macronutrients?.map((nutrient, index) => (
                  <tr
                    key={nutrient.title}
                    data-testid={`${testId}-macronutrientRow-${index}`}
                    className="border-b border-gray-300 last:border-b-0"
                  >
                    <td 
                      data-testid={`${testId}-macronutrientName-${index}`}
                      className="py-2 typography-h3 text-[#F4F4F5] text-left"
                    >
                      {nutrient.title}
                    </td>
                    <td 
                      data-testid={`${testId}-macronutrientValue-${index}`}
                      className="py-2 typography-h3 text-[#F4F4F5] text-right"
                    >
                      {nutrient.value}g
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tips to Consume */}
      {tips?.length > 0 && (
        <div 
          data-testid={`${testId}-tipsSection`}
          className="pb-16"
        >
          <h2 
            data-testid={`${testId}-tipsTitle`}
            className="typography-h3 text-[#F4F4F5] mb-4"
          >
            Tips to Consume
          </h2>
          <ul 
            data-testid={`${testId}-tipsList`}
            className="space-y-4"
          >
            {tips?.map((tip, index) => (
              <li 
                key={index} 
                data-testid={`${testId}-tip-${index}`}
                className="typography-h2 text-[#F4F4F5] font-serif"
              >
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default FoodInfoCard;
